/**
 * Resolve the PRODUCT id of a server/Ably order item.
 *
 * The Django serializer (OrderItemsSyncSerializer) emits `product` as a plain
 * number (PrimaryKeyRelatedField) and has NO `product_id` field, while
 * `orderItem.id` is the OrderItem row PK — a completely different id space.
 * Every cart-reconciliation match must therefore go through this helper;
 * falling back to `orderItem.id` (as older inline chains did) makes the match
 * silently impossible and re-shows already-sent items as "new".
 *
 * @param {Object} orderItem - Item from an API response or Ably payload
 * @returns {number|string|null} The product id, or null when the item has no
 *   product reference (e.g. custom/open items) — callers must treat null as
 *   "no match" rather than matching two null-product items together.
 */
export function getOrderItemProductId(orderItem) {
  if (!orderItem) return null;
  const product = orderItem.product;
  if (product != null) {
    return typeof product === "object" ? product.id ?? null : product;
  }
  return orderItem.product_id != null ? orderItem.product_id : null;
}

/**
 * Calculate item totals, discounts, and modifiers text
 * @param {Object} params - Calculation parameters
 * @param {number} params.price - Item base price
 * @param {Array} params.modifiers - Array of modifiers
 * @param {number} params.quantity - Item quantity
 * @param {number} params.discount - Discount amount
 * @param {number} params.wholeDiscount - Whole discount amount
 * @returns {Object} Calculated values
 */
export function calculateItemTotals({
  price,
  modifiers = [],
  quantity = 1,
  discount = 0,
  wholeDiscount = 0
}) {
  const modifiersTotal = modifiers.reduce(
    (sum, mod) => sum + (mod.price || 0),
    0
  );
  const baseTotal = (price + modifiersTotal) * quantity;
  const itemTotal = baseTotal - discount - wholeDiscount;
  const itemTotalWithoutDiscount = baseTotal;
  const hasDiscount = discount + wholeDiscount > 0;
  const modifiersText =
    modifiers && modifiers.length > 0
      ? modifiers.map((m) => m.name).join(", ")
      : null;

  return {
    modifiersTotal,
    baseTotal,
    itemTotal,
    itemTotalWithoutDiscount,
    hasDiscount,
    modifiersText
  };
}

/**
 * Match tax rate by tax_rate_value if tax_rates array is empty
 * @param {Object} params - Tax rate matching parameters
 * @param {Array} params.itemTaxRates - Current tax rates array
 * @param {number} params.taxRateValue - Tax rate value to match
 * @param {Array} params.availableTaxRates - Available tax rates from store
 * @returns {Array} Matched tax rates array
 */
export function matchTaxRateByValue({
  itemTaxRates = [],
  taxRateValue,
  availableTaxRates = []
}) {
  if (
    itemTaxRates.length === 0 &&
    taxRateValue != null &&
    availableTaxRates.length > 0
  ) {
    const matchingTaxRate = availableTaxRates.find(
      (tr) => Math.abs(tr.rate - taxRateValue) < 0.01
    );
    if (matchingTaxRate) {
      return [matchingTaxRate.id];
    }
  }
  return itemTaxRates;
}

export function normalizeNote(noteValue) {
  return (noteValue || "").trim().toLowerCase();
}

export function getModifierId(mod) {
  return mod.oryginal || mod.id || mod.original || null;
}

export function compareModifiers(mods1, mods2) {
  if (mods1.length === 0 && mods2.length === 0) return true;
  if (mods1.length !== mods2.length) return false;
  if (mods1.length === 0) return true;

  const sorted1 = [...mods1]
    .map((mod) => ({ id: getModifierId(mod), mod }))
    .filter((m) => m.id !== null)
    .sort((a, b) => {
      const idA = String(a.id);
      const idB = String(b.id);
      return idA < idB ? -1 : idA > idB ? 1 : 0;
    });

  const sorted2 = [...mods2]
    .map((mod) => ({ id: getModifierId(mod), mod }))
    .filter((m) => m.id !== null)
    .sort((a, b) => {
      const idA = String(a.id);
      const idB = String(b.id);
      return idA < idB ? -1 : idA > idB ? 1 : 0;
    });

  if (sorted1.length !== sorted2.length) return false;

  return sorted1.every((mod1, index) => {
    const mod2 = sorted2[index];
    return String(mod1.id) === String(mod2.id);
  });
}

export function findMatchingCartItem(
  cartItems,
  newItem,
  newModifiers,
  newNote
) {
  return cartItems.find((cartItem) => {
    if (cartItem.isSent === true) {
      return false;
    }

    if (cartItem.id !== newItem.id) {
      return false;
    }

    const cartModifiers = cartItem.selectedModifiers || [];
    if (!compareModifiers(cartModifiers, newModifiers)) {
      return false;
    }

    const cartNote = normalizeNote(cartItem.note);
    const normalizedNewNote = normalizeNote(newNote);
    if (cartNote !== normalizedNewNote) {
      return false;
    }

    return true;
  });
}

export function compareNewOrderWithCart(newOrderItems, currentCartItems) {
  if (!newOrderItems || newOrderItems.length === 0) {
    return {
      newItemsInOrder: [],
      modifiedItems: [],
      removedItems: currentCartItems
        .filter((item) => item.isSent === true)
        .map((item) => ({
          cartId: item.cartId,
          name: item.name,
          quantity: item.quantity
        })),
      unchangedItems: []
    };
  }

  if (!currentCartItems || currentCartItems.length === 0) {
    return {
      newItemsInOrder: newOrderItems.map((item) => ({
        productId: getOrderItemProductId(item),
        name: item.name || item.product?.name || "",
        quantity: item.quantity || 1
      })),
      modifiedItems: [],
      removedItems: [],
      unchangedItems: []
    };
  }

  const sentCartItems = currentCartItems.filter((item) => item.isSent === true);

  const newItemsInOrder = [];
  const modifiedItems = [];
  const removedItems = [];
  const unchangedItems = [];
  const matchedCartItems = new Set();

  newOrderItems.forEach((orderItem) => {
    const orderProductId = getOrderItemProductId(orderItem);
    const orderModifiers =
      orderItem.modifiers || orderItem.modifier_items || [];
    const orderNote = normalizeNote(orderItem.note || orderItem.notes || "");
    const orderQuantity = orderItem.quantity || 1;

    const matchingCartItem = sentCartItems.find((cartItem) => {
      const cartProductId = cartItem.id ?? cartItem.product?.id ?? null;
      const cartModifiers =
        cartItem.selectedModifiers || cartItem.modifiers || [];
      const cartNote = normalizeNote(cartItem.note || "");

      if (
        orderProductId == null ||
        cartProductId == null ||
        String(orderProductId) !== String(cartProductId)
      ) {
        return false;
      }

      if (!compareModifiers(orderModifiers, cartModifiers)) {
        return false;
      }

      if (orderNote !== cartNote) {
        return false;
      }

      return true;
    });

    if (!matchingCartItem) {
      newItemsInOrder.push({
        productId: orderProductId,
        name: orderItem.name || orderItem.product?.name || "",
        quantity: orderQuantity,
        modifiers: orderModifiers,
        note: orderNote
      });
    } else {
      matchedCartItems.add(matchingCartItem.cartId);
      const cartQuantity = matchingCartItem.quantity || 1;

      if (orderQuantity !== cartQuantity) {
        modifiedItems.push({
          cartId: matchingCartItem.cartId,
          name: matchingCartItem.name,
          oldQuantity: cartQuantity,
          newQuantity: orderQuantity,
          productId: orderProductId,
          modifiers: orderModifiers,
          note: orderItem.note || orderItem.notes || ""
        });
      } else {
        unchangedItems.push({
          cartId: matchingCartItem.cartId,
          name: matchingCartItem.name,
          quantity: orderQuantity
        });
      }
    }
  });

  sentCartItems.forEach((cartItem) => {
    if (!matchedCartItems.has(cartItem.cartId)) {
      removedItems.push({
        cartId: cartItem.cartId,
        name: cartItem.name,
        quantity: cartItem.quantity || 1
      });
    }
  });

  return {
    newItemsInOrder,
    modifiedItems,
    removedItems,
    unchangedItems
  };
}

export function prepareCartItem(
  item,
  quantity,
  note,
  modifiers,
  taxRates,
  existingOrderItems = null
) {
  const itemId = `${item.id}-${Date.now()}`;

  let itemTaxRates =
    item.tax_rates ||
    (item.product && item.product.tax_rates ? item.product.tax_rates : []);

  itemTaxRates = matchTaxRateByValue({
    itemTaxRates,
    taxRateValue: item.tax_rate_value,
    availableTaxRates: taxRates
  });

  const product = {
    id: item.id,
    tax_rates: itemTaxRates
  };

  let isNew = true;
  let isModified = false;
  let originalQuantity = null;

  if (existingOrderItems && existingOrderItems.length > 0) {
    const matchingExistingItem = existingOrderItems.find((existingItem) => {
      const existingProductId = getOrderItemProductId(existingItem);
      const cartProductId = item.id ?? item.product?.id ?? null;

      if (
        existingProductId == null ||
        cartProductId == null ||
        String(existingProductId) !== String(cartProductId)
      ) {
        return false;
      }

      const existingModifiers =
        existingItem.modifiers || existingItem.modifier_items || [];
      const cartModifiers = modifiers || [];
      if (!compareModifiers(existingModifiers, cartModifiers)) {
        return false;
      }

      const existingNote = normalizeNote(
        existingItem.note || existingItem.notes || ""
      );
      const cartNote = normalizeNote(note || "");
      if (existingNote !== cartNote) {
        return false;
      }

      return true;
    });

    if (matchingExistingItem) {
      const existingQuantity = matchingExistingItem.quantity || 1;
      if (existingQuantity !== quantity) {
        isNew = false;
        isModified = true;
        originalQuantity = existingQuantity;
      } else {
        isNew = false;
        isModified = false;
      }
    } else {
      isNew = true;
      isModified = false;
    }
  }

  const {
    itemTotal,
    itemTotalWithoutDiscount,
    hasDiscount,
    modifiersText
  } = calculateItemTotals({
    price: item.price,
    modifiers: modifiers || [],
    quantity,
    discount: item.discount || 0,
    wholeDiscount: item.wholeDiscount || 0
  });

  return {
    ...item,
    cartId: itemId,
    local_id: itemId,
    quantity,
    note: note || "",
    selectedModifiers: modifiers || [],
    modifiers: modifiers || [],
    product: product,
    tax_rate_value: item.tax_rate_value || 0,
    tax_status: item.tax_rate_value ? 0 : 1,
    canRemove: item.canRemove !== undefined ? item.canRemove : true,
    isSent: false,
    isNew: isNew,
    isModified: isModified,
    originalQuantity: originalQuantity,
    itemTotal: itemTotal,
    itemTotalWithoutDiscount: itemTotalWithoutDiscount,
    hasDiscount: hasDiscount,
    modifiersText: modifiersText
  };
}
